import { useCallback, useEffect, useRef, useState } from "react";
import { useApolloClient } from "@apollo/client";
import {
  GetCmpManageInvoicesInput,
  GetCmpManageInvoicesDocument,
  GetCmpManageInvoicesQuery,
  GetCmpManageInvoicesQueryVariables,
} from "../../../../graphql/types";
import { getPreserveExpectedErrorsForCallerContext } from "../../../../utils/apolloErrorPolicy";

export const invoiceReadApolloContext = getPreserveExpectedErrorsForCallerContext({
  expectedGraphQLErrorCodes: [],
});

// API Gateway ends a request at 30 s. A read that never settles (a lost
// connection) must not stop the screen from reading again.
export const ACTIVE_READ_WAIT_LIMIT_MS = 35000;

const waitForActiveRead = (settled: Promise<void>) => new Promise<void>(resolve => {
  const timer = window.setTimeout(resolve, ACTIVE_READ_WAIT_LIMIT_MS);
  void settled.then(() => { window.clearTimeout(timer); resolve(); });
});

type PagedInvoiceState = {
  key: string;
  data?: GetCmpManageInvoicesQuery;
  error?: Error;
  loading: boolean;
};

/**
 * A page and its full filtered totals are published together. Old requests cannot replace it.
 *
 * `previousData` is the last complete page for the same chamber. A page, sort,
 * filter, search or refetch keeps it on screen (dimmed) while the next page
 * loads, so the table does not empty and the page does not jump (IF10 G2,
 * audit T-3). `data` stays exact: it is only the page for the current request.
 *
 * Each request reads the whole chamber, and a server cannot stop a read after
 * the browser leaves it. Many parallel reads throttled Thunder Bay's index
 * (Nov 2 2026), so one chamber has one read at a time here. A request made
 * during a read waits for it, and only the newest waiting request is sent. A
 * refresh after a save always sends its own read, so it cannot show older data.
 */
export const usePagedManageInvoices = (input: GetCmpManageInvoicesInput) => {
  const client = useApolloClient();
  const requestKey = JSON.stringify(input);
  const chamberId = input.chamberId;
  const generation = useRef(0);
  const activeRead = useRef<{ chamberId: string; settled: Promise<void> } | null>(null);
  const currentRequestKey = useRef(requestKey);
  currentRequestKey.current = requestKey;
  const [state, setState] = useState<PagedInvoiceState>({
    key: requestKey, loading: Boolean(chamberId),
  });
  const [lastPage, setLastPage] = useState<{ chamberId?: string | null; data: GetCmpManageInvoicesQuery } | null>(null);

  const refetch = useCallback(async (): Promise<{ data: GetCmpManageInvoicesQuery }> => {
    if (currentRequestKey.current !== requestKey) {
      throw new Error("The invoice request was replaced by a newer request.");
    }
    const requestGeneration = ++generation.current;
    setState({ key: requestKey, loading: true });
    try {
      if (!chamberId) throw new Error("Chamber context is required to load invoices.");
      const active = activeRead.current;
      if (active?.chamberId === chamberId) {
        await waitForActiveRead(active.settled);
        if (requestGeneration !== generation.current) {
          throw new Error("The invoice request was replaced by a newer request.");
        }
      }
      const requestInput: GetCmpManageInvoicesInput = JSON.parse(requestKey);
      const query = client.query<GetCmpManageInvoicesQuery, GetCmpManageInvoicesQueryVariables>({
        query: GetCmpManageInvoicesDocument,
        variables: { input: requestInput },
        fetchPolicy: "no-cache",
        context: invoiceReadApolloContext,
      });
      const read = {
        chamberId,
        settled: query.then(() => undefined, () => undefined).then(() => {
          if (activeRead.current === read) activeRead.current = null;
        }),
      };
      activeRead.current = read;
      const result = await query;
      if (requestGeneration !== generation.current) {
        throw new Error("The invoice request was replaced by a newer request.");
      }
      const page = result.data?.getCmpManageInvoices;
      const expectedRows = page ? Math.min(requestInput.limit || 25, Math.max(0, page.totalCount - (requestInput.offset || 0))) : 0;
      if (!page || !Number.isSafeInteger(page.totalCount) || page.totalCount < 0 ||
        page.rows.length !== expectedRows || new Set(page.rows.map(row => row.id)).size !== page.rows.length) {
        throw new Error("The invoice page response is incomplete. Retry the request.");
      }
      setState({ key: requestKey, data: result.data, loading: false });
      setLastPage({ chamberId, data: result.data });
      return { data: result.data };
    } catch (error: unknown) {
      const failure = error instanceof Error ? error : new Error("Invoice records could not be loaded.");
      if (requestGeneration === generation.current) {
        setState({ key: requestKey, error: failure, loading: false });
      }
      throw failure;
    }
  }, [chamberId, client, requestKey]);

  useEffect(() => {
    if (chamberId) {
      // The hook retains the error for the page's visible retry state.
      void refetch().catch(() => undefined);
    } else {
      setState({ key: requestKey, loading: false });
    }
    return () => { generation.current += 1; };
  }, [chamberId, refetch, requestKey]);

  const current = state.key === requestKey;
  return {
    data: current ? state.data : undefined,
    error: current ? state.error : undefined,
    loading: Boolean(chamberId) && (!current || state.loading),
    previousData: chamberId && lastPage?.chamberId === chamberId ? lastPage.data : undefined,
    refetch,
  };
};
